/**
 * tRPC 클라이언트 설정 및 유틸리티입니다.
 *
 * 주요 기능:
 * - tRPC React 클라이언트 생성
 * - WebSocket/HTTP 링크 분기 처리
 * - SuperJSON 데이터 변환기 적용
 *
 * @remarks
 * engine 백엔드와의 타입-세이프 API 통신에 사용됩니다.
 * 구독(subscription)은 WebSocket, 나머지는 HTTP로 전송합니다.
 */
import type { AppRouter } from '../../../engine/src/routers/app.router';
import { createTRPCReact } from '@trpc/react-query';
import superjson from 'superjson';
import { httpBatchLink, wsLink, createWSClient, splitLink, httpLink } from '@trpc/client';
import { registerSuperJsonError } from '@bit-box/common';
// import { getApiUrl, getHostPort } from './utils';

// SuperJSON에 AppError 등록
// Backend와 동일하게 등록해야 직렬화/역직렬화가 정상 작동
registerSuperJsonError();

/** tRPC React 클라이언트 */
export const trpc = createTRPCReact<AppRouter>();

/**
 * tRPC 링크 설정을 생성합니다.
 *
 * @param hostPort - 서버 호스트:포트
 * @param apiUrl - API 엔드포인트 URL
 * @returns 구독은 WebSocket, 나머지는 HTTP(batch)를 사용하는 splitLink
 */
export function getEndingLink(hostPort: string, apiUrl: string, token?: string) {
  // SSR 환경에서는 요청 전달하지 않음
  if (typeof window === 'undefined') {
    return httpBatchLink({
      url: '',
      /**
       * @link https://trpc.io/docs/data-transformers
       */
      transformer: superjson,
    });
  }

  // Authorization 헤더 (cloud 모드에서 토큰이 있을 때)
  const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

  // http link 속성
  const linkOptions = {
    url: typeof window === 'undefined' ? '' : apiUrl,
    fetch: (url: URL | RequestInfo, options: RequestInit /* | RequestInitEsque */ | undefined) => {
      // sessionStorage 에 따라 달라지는 URL을 fetch 커스터마이징으로 바꿔줘야 됨
      const urlInfo = new URL(url.toString());
      // getApiUrl 매번 호출해서 사용하지 않으면 여러 탭으로 접속 시 동일 포트 사용하는 오류 발생
      urlInfo.host = hostPort;
      const newUrl = urlInfo.toString();
      return fetch(newUrl, {
        ...options,
        credentials: 'include',
        headers: {
          ...options?.headers,
          ...authHeaders,
        },
      });
    },
    transformer: superjson,
  };

  // WebSocket URL: apiUrl(/trpc 포함)
  // cloud 모드: https://engine-dev.sageant.com/bot/1/trpc → wss://engine-dev.sageant.com/bot/1/trpc
  // Electron 모드: http://localhost:3001/trpc → ws://localhost:3001/trpc
  const wsUrl = apiUrl.replace(/^http/, 'ws');

  const wsClient = createWSClient({
    url: wsUrl || `ws://${hostPort}`,
    connectionParams: token ? () => ({ authorization: token }) : undefined,
  });

  // call subscriptions through 'websocket' and the rest over 'http'
  return splitLink({
    condition(op) {
      if (op.type === 'subscription') {
        // console.log(`op.type === ${op.type}`, op);
      }
      return op.type === 'subscription';
    },
    // 구독이므로 웹소켓 사용
    true: wsLink<AppRouter>({
      client: wsClient,
      transformer: superjson,
    }),
    // 구독이 아니므로 http 사용
    false: splitLink({
      // app.info, app.licenses 경로는 낱개로 보내고 나머지는 batch로 보냄
      // 로그인이나 이용권 선택은 인증이 필요한 다른 요청의 실패에 영향 받지 않아야 하므로
      condition(op) {
        return op.path.startsWith('app.');
      },
      true: httpLink(linkOptions),
      false: httpBatchLink(linkOptions),
    }),
  });
}

/**
 * @deprecated
 * 2023-10-17 buruss 사용하지 않기로 함
 * 원래는 _app.tsx 맨 밑에서 trpcNext.withTRPC로 감싸서 사용했음
 * 하지만 이렇게 하면 로그인 페이지(index.tsx)에서 아직 봇 번호가 결정되지 않은 상태에서
 * trpc용 websocket이 3000 포트로 연결되어서
 * 봇 선택하고 로그인한 후에도 계속 3000 포트로 연결되는 문제가 있음.
 * 그래서 로그인 페이지(index.tsx)에서는 trpc를 아예 사용하지 않기로 함.
 * 대신, trpc로 감싸주는 역할은 default-layout.tsx 및 bot-layout.tsx 에서 함.
 */
// export const trpcNext = createTRPCNext<AppRouter>({
//   config({ ctx }) {
//     /**
//      * If you want to use SSR, you need to use the server's full URL
//      * @link https://trpc.io/docs/ssr
//      */

//     return {
//       /**
//        * @link https://trpc.io/docs/links
//        */
//       links: [
//         // adds pretty logs to your console in development and logs errors in production
//         getEndingLink(),
//       ],
//       /**
//        * @link https://react-query.tanstack.com/reference/QueryClient
//        */
//       queryClientConfig: { defaultOptions: { queries: { staleTime: 60 } } },
//       headers: () => {
//         if (ctx?.req) {
//           // on ssr, forward client's headers to the server
//           return {
//             ...ctx.req.headers,
//             'x-ssr': '1',
//           };
//         }
//         return {};
//       },
//     };
//   },
//   /**
//    * @link https://trpc.io/docs/ssr
//    */
//   ssr: false,

//   /**
//    * @link https://trpc.io/docs/data-transformers
//    */
//   transformer: superjson,
// });
